import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@dorixona/ui'
import { Field } from './ui/Field'
import { DateInput } from './ui/DateInput'
import { Segmented } from './ui/Segmented'
import { AuthHero } from './AuthHero'
import { GENDERS, MAX_BIRTH_DATE, type RegistrationForm, registrationSchema } from './model'

export interface RegisterStepProps {
  /** Имя из Telegram — покупатель поправит, если нужно. */
  firstName?: string
  onSubmit: (form: RegistrationForm) => void
  pending: boolean
  error: string | undefined
}

export function RegisterStep({ firstName = '', onSubmit, pending, error }: RegisterStepProps) {
  const { t } = useTranslation('auth')
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<RegistrationForm>({
    resolver: zodResolver(registrationSchema),
    defaultValues: { firstName, lastName: '', gender: undefined, birthDate: '' },
  })

  const gender = watch('gender')
  /** Сообщения схемы — ключи перевода: текст выбирает экран. */
  const message = (key: string | undefined) => (key ? t(key) : undefined)

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event)
      }}
      noValidate
    >
      <AuthHero title={t('register.title')} subtitle={t('register.subtitle')} />

      <Field required label={t('register.firstName')} error={message(errors.firstName?.message)}>
        {({ id, describedBy }) => (
          <input
            id={id}
            autoComplete="given-name"
            aria-required
            placeholder={t('register.firstNamePlaceholder')}
            enterKeyHint="next"
            aria-describedby={describedBy}
            {...register('firstName')}
          />
        )}
      </Field>

      <Field
        label={t('register.lastName')}
        optional={t('register.optional')}
        error={message(errors.lastName?.message)}
      >
        {({ id, describedBy }) => (
          <input
            id={id}
            autoComplete="family-name"
            placeholder={t('register.lastNamePlaceholder')}
            enterKeyHint="next"
            aria-describedby={describedBy}
            {...register('lastName')}
          />
        )}
      </Field>

      <Field required label={t('register.gender')} error={message(errors.gender?.message)}>
        {({ describedBy }) => (
          <Segmented
            label={t('register.gender')}
            describedBy={describedBy}
            required
            value={gender}
            options={GENDERS.map((value) => ({ value, label: t(`register.${value}`) }))}
            onChange={(value) => {
              setValue('gender', value, { shouldValidate: true })
            }}
          />
        )}
      </Field>

      <Field
        label={t('register.birthDate')}
        optional={t('register.optional')}
        hint={t('register.birthDateHint')}
        error={message(errors.birthDate?.message)}
      >
        {({ id, describedBy }) => (
          <DateInput
            id={id}
            max={MAX_BIRTH_DATE}
            aria-describedby={describedBy}
            {...register('birthDate')}
          />
        )}
      </Field>

      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}

      <Button type="submit" loading={pending}>
        {t('register.submit')}
      </Button>
    </form>
  )
}
